import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type { HeadOfficeMatch } from '@/types';

/**
 * The exact head-office picker behind report pickup's "Client / head office" field.
 *
 * Free text used to be the only option, resolved against `Dataset.Customer` best-effort. That
 * table is a Power BI-side copy and can disagree with the record the rest of Key Travel treats as
 * authoritative for a client's identity, which produces exactly the failure this component exists
 * to close: a pickup folder named `{HeadOfficeId} - {Name}` that does not match the head office it
 * claims to. Choosing a suggestion here sets both fields at once, verbatim, from the same Profiles
 * API row — never independently, and never typed.
 *
 * Free text is still the fallback, deliberately. The Profiles API being unreachable, or a client
 * genuinely not existing in it yet, must never stop somebody switching pickup on — it only means
 * the folder is filed by name alone until an exact match is chosen, the same degrade-to-name
 * behaviour `PickupStorageLayout.ClientFolderName` already has on the server side.
 */
export function PickupHeadOfficePicker({
  name,
  headOfficeId,
  onChange,
  placeholder = 'e.g. University of Manchester'
}: {
  /** The client name as the caller currently holds it — free text until a suggestion is chosen. */
  name: string;
  /** The exact head-office id, once a suggestion has been chosen; null while it is still free text. */
  headOfficeId: string | null;
  onChange: (value: { name: string; headOfficeId: string | null }) => void;
  placeholder?: string;
}) {
  const api = useApi();
  const [search, setSearch] = useState(name);
  const [debounced, setDebounced] = useState(search.trim());
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // The caller's value can change from outside (a suggested client name arriving once report
  // parameters resolve, a delivery loading on the detail page) — keep the box in step with it.
  useEffect(() => setSearch(name), [name]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['pickup-head-office-search', debounced],
    queryFn: () => api.pickupHeadOfficeSearch(debounced),
    enabled: open && debounced.length >= 2,
    staleTime: 60_000,
    retry: false
  });

  const matches = data?.matches ?? [];
  // A request that throws (network error, non-2xx) and a response that came back but says
  // `available: false` (no APIM subscription key, or a failed upstream call) both mean the same
  // thing to this field: Profiles could not be asked, so it must not be shown as "no matches".
  const unavailable = isError || data?.available === false;

  function choose(match: HeadOfficeMatch) {
    setSearch(match.name);
    setOpen(false);
    onChange({ name: match.name, headOfficeId: String(match.headOfficeId) });
  }

  function typeFreeText(value: string) {
    setSearch(value);
    setOpen(true);
    // Typing again after a match was chosen means it may no longer apply — cleared rather than
    // left attached to whatever was typed next, which would silently rename the wrong head office.
    onChange({ name: value, headOfficeId: null });
  }

  // "Confirmed" means the box still holds exactly what was chosen — editing it a single character
  // has to drop the match, because the id is only exact for the name it came with.
  const confirmed = Boolean(headOfficeId) && search.trim() === name.trim() && name.trim().length > 0;
  const showMatches = open && debounced.length >= 2;

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        className="input"
        placeholder={placeholder}
        value={search}
        onFocus={() => setOpen(true)}
        onChange={e => typeFreeText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        autoComplete="off"
      />

      {showMatches && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: 2,
            maxHeight: 220,
            overflowY: 'auto',
            listStyle: 'none',
            padding: 4,
            background: 'var(--kt-white)',
            border: '1px solid var(--kt-grey-200)',
            borderRadius: 6,
            boxShadow: 'var(--shadow-pop)'
          }}
        >
          {isFetching && (
            <li style={{ padding: '6px 10px', fontSize: '.85em', color: 'var(--kt-grey-400)' }}>
              Searching…
            </li>
          )}
          {!isFetching && unavailable && (
            <li style={{ padding: '6px 10px', fontSize: '.85em', color: 'var(--kt-amber, #a06800)' }}>
              Head office search is unavailable right now. Keep typing to file by name only.
            </li>
          )}
          {!isFetching && !unavailable && matches.length === 0 && (
            <li style={{ padding: '6px 10px', fontSize: '.85em', color: 'var(--kt-grey-400)' }}>
              No head office matches "{debounced}".
            </li>
          )}
          {!isFetching && !unavailable && matches.map(m => (
            <li key={m.headOfficeId}>
              <button
                type="button"
                onClick={() => choose(m)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 10px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  borderRadius: 4,
                  font: 'inherit',
                  color: 'inherit'
                }}
              >
                <span>{m.name}</span>
                <span style={{ color: 'var(--kt-grey-400)', fontSize: '.85em', marginLeft: 10 }}>
                  {m.headOfficeId}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {confirmed ? (
        <div className="field-hint" style={{ color: 'var(--kt-green-dark)' }}>
          Matched head office {headOfficeId} — the folder will be named exactly "{headOfficeId} - {name}".
        </div>
      ) : (
        <div className="field-hint">
          {search.trim()
            ? 'Pick a suggestion for an exact match, or keep typing to file by name alone.'
            : 'Files are filed and searched under this name.'}
        </div>
      )}
    </div>
  );
}
